import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Camera, CheckCircle2, ClipboardCheck, LogIn, Shirt, UserCog, X,
} from 'lucide-react';
import {
  supervisorBoard, supervisorSetTrial, supervisorComplete, supervisorLoginAsWorker,
} from '../api/resources';
import { assetUrl } from '../utils/format';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { portalMe } from '../api/resources';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const ROLE_WORD = { CUTTING_MASTER: 'Cutting master', SEWING_TAILOR: 'Tailor', ALL_ROUNDER: 'All-rounder', NO_TRADE: 'Other' };
const WORK_WORD = { CUTTING: 'Cutting', STITCHING: 'Stitching' };

/**
 * The supervisor's desk. Three things in one place, in the order the day runs:
 * the work waiting to be looked at (with the photo the worker sent in), the
 * customers coming for a trial, and the orders that only need the word.
 *
 * The people are along the top, because a supervisor's first question is
 * usually "who is on what", and from there they can step into anyone's screen.
 */
export default function Supervisor() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [photo, setPhoto] = useState(null); // full-size view
  const [signOff, setSignOff] = useState(null); // order awaiting the final word

  // The same desk serves an office login and a supervisor on the floor; what
  // differs is which screens they are allowed to walk to from here.
  const { role } = useAuth();
  const onFloor = role === 'WORKER';
  const { data: me } = useQuery({ queryKey: ['portal-me'], queryFn: portalMe, enabled: onFloor, staleTime: 60_000 });
  const mayOpenOrder = !onFloor || !!me?.access?.orders;
  const mayEditOrder = !onFloor || !!me?.access?.createOrder;
  const openOrder = (invoiceId) => {
    if (!mayOpenOrder) return;
    nav(mayEditOrder ? `/invoices/${invoiceId}/edit` : `/invoices/${invoiceId}`, { state: { from: onFloor ? '/portal/supervisor' : '/supervisor' } });
  };

  const { data, isLoading, isError } = useQuery({ queryKey: ['supervisor-board'], queryFn: supervisorBoard });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['supervisor-board'] });
    qc.invalidateQueries({ queryKey: ['invoices'] });
  };

  const trialMut = useMutation({
    mutationFn: ({ id, done }) => supervisorSetTrial(id, done),
    onSuccess: (d) => { success(d.message); refresh(); },
    onError: (e) => error(e.response?.data?.message || 'Could not record the trial.'),
  });

  const completeMut = useMutation({
    mutationFn: (id) => supervisorComplete(id),
    onSuccess: (d) => { success(d.message); setSignOff(null); refresh(); },
    onError: (e) => { error(e.response?.data?.message || 'Could not finish the order.'); setSignOff(null); },
  });

  const loginAs = useMutation({
    mutationFn: (workerId) => supervisorLoginAsWorker(workerId),
    onSuccess: (d) => {
      // Keep our own session aside so "back to my screen" is one tap.
      localStorage.setItem('erp_supervisor_token', localStorage.getItem('erp_token') || '');
      localStorage.setItem('erp_acting_as', d.worker.name);
      localStorage.setItem('erp_acting_from', onFloor ? '/portal/supervisor' : '/supervisor');
      localStorage.setItem('erp_token', d.token);
      success(d.message);
      window.location.assign('/portal');
    },
    onError: (e) => error(e.response?.data?.message || 'Could not open that screen.'),
  });

  if (isLoading) return <Spinner label="Looking at the floor…" />;
  if (isError || !data) {
    return <div className="card p-10 text-center text-sm text-content-muted">Could not load the supervisor board.</div>;
  }

  const { workers = [], inspections = [], trials = [], ready = [], counts = {} } = data;

  return (
    <div className="space-y-5">
      <PageHeader
        title={onFloor ? 'Supervise' : 'Supervisor'}
        subtitle="Check the work, mark the trials, and sign off finished orders"
        icon={ClipboardCheck}
      />

      {/* who is on what */}
      <div className="card p-4">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-content">{onFloor ? 'Your team today' : 'The team today'}</h3>
        {workers.length === 0 ? (
          <p className="py-6 text-center text-sm italic text-content-subtle">Nobody to supervise yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {workers.map((w) => (
              <div key={w.id} className="rounded-lg border border-line p-3">
                <div className="flex items-baseline gap-2">
                  <b className="truncate text-sm text-content">{w.name}</b>
                  <span className="text-2xs text-content-subtle">{ROLE_WORD[w.role] || w.role}</span>
                </div>
                <p className="mt-1 text-xs text-content-muted">
                  <b className="tabular-nums text-content">{w.running}</b> working now ·{' '}
                  <b className="tabular-nums text-content">{w.waiting}</b> waiting ·{' '}
                  <b className="tabular-nums text-content">{w.finishedToday}</b> done today
                </p>
                {w.awaitingCheck > 0 && (
                  <p className="mt-1 inline-block rounded-full bg-warning-50 px-2 py-0.5 text-2xs font-bold text-warning-700">
                    {w.awaitingCheck} waiting for your check
                  </p>
                )}
                <div className="mt-2 flex gap-2">
                  {!onFloor && (
                    <button className="btn-ghost btn-sm" onClick={() => nav(`/workers/${w.id}`, { state: { from: '/supervisor' } })}>
                      <UserCog size={13} /> Record
                    </button>
                  )}
                  <button
                    className="btn-outline btn-sm"
                    disabled={!w.canLoginAs || loginAs.isPending}
                    title={w.canLoginAs ? `Work on ${w.name}'s screen` : 'This worker has no portal login yet'}
                    onClick={() => loginAs.mutate(w.id)}
                  >
                    <LogIn size={13} /> Login as worker
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* finished work, with the photos */}
      <div className="card p-4">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-content">Work to check</h3>
          <span className="rounded-full bg-brand-600 px-1.5 text-2xs font-bold text-white">{counts.inspections || 0}</span>
          <span className="text-2xs text-content-subtle">Each photo is what the worker sent in when they finished</span>
        </div>
        {inspections.length === 0 ? (
          <p className="py-6 text-center text-sm italic text-content-subtle">Nothing is waiting to be checked.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {inspections.map((j) => (
              <div key={j.assignmentId} className="rounded-lg border border-line p-3">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <b className="text-sm text-content">{j.itemName}</b>
                  <span className="text-2xs text-content-subtle">×{j.qty}</span>
                  <span className="ml-auto rounded-full bg-surface-sunken px-2 py-0.5 text-2xs font-semibold text-content-muted">
                    {j.stageLabel}
                  </span>
                </div>
                <p className="text-xs text-content-muted">
                  {WORK_WORD[j.workType]} by <b className="text-content">{j.worker || 'someone'}</b> · order{' '}
                  <button className="font-semibold text-brand-700 underline" onClick={() => openOrder(j.invoiceId)}>
                    {j.orderNo}
                  </button>{' '}
                  · {j.customer}
                </p>

                {j.photos.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {j.photos.map((src) => (
                      <button key={src} type="button" onClick={() => setPhoto({ src, job: j })} title="Open the photo">
                        <img src={assetUrl(src)} alt="" className="h-16 w-16 rounded border border-line object-cover" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 flex items-center gap-1 text-2xs font-semibold text-warning-700">
                    <Camera size={12} /> No photo was sent with this one
                  </p>
                )}

                {j.note && <p className="mt-1.5 text-xs italic text-content-muted">“{j.note}”</p>}
                {j.remarks && <p className="mt-0.5 text-2xs text-content-subtle">Order note: {j.remarks}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* trials */}
        <div className="card p-4">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-content">Trials</h3>
            <span className="rounded-full bg-warning-500 px-1.5 text-2xs font-bold text-white">{counts.trials || 0}</span>
            <span className="text-2xs text-content-subtle">Mark it done once the customer has tried the garment</span>
          </div>
          {trials.length === 0 ? (
            <p className="py-6 text-center text-sm italic text-content-subtle">No trials are pending.</p>
          ) : (
            <ul className="divide-y divide-line">
              {trials.map((t) => (
                <li key={t.invoiceId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <button className="text-sm font-semibold text-brand-700 underline" onClick={() => openOrder(t.invoiceId)}>
                    {t.orderNo}
                  </button>
                  <span className="text-sm text-content">{t.customer}</span>
                  <span className="text-2xs text-content-subtle">{t.items}</span>
                  <span className={`text-xs font-semibold ${t.overdue ? 'text-danger-600' : 'text-content-muted'}`}>
                    {t.overdue && <AlertTriangle size={12} className="mr-1 inline" />}
                    {t.trialDate}
                  </span>
                  <button
                    className="btn-outline btn-sm ml-auto"
                    disabled={trialMut.isPending}
                    onClick={() => trialMut.mutate({ id: t.invoiceId, done: true })}
                  >
                    <CheckCircle2 size={13} /> Trial done
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* final sign-off */}
        <div className="card p-4">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-content">Ready for your sign-off</h3>
            <span className="rounded-full bg-success-600 px-1.5 text-2xs font-bold text-white">{counts.ready || 0}</span>
            <span className="text-2xs text-content-subtle">An order is only finished once you say so</span>
          </div>
          {ready.length === 0 ? (
            <p className="py-6 text-center text-sm italic text-content-subtle">Nothing is waiting on you.</p>
          ) : (
            <ul className="divide-y divide-line">
              {ready.map((r) => (
                <li key={r.invoiceId} className="py-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button className="text-sm font-semibold text-brand-700 underline" onClick={() => openOrder(r.invoiceId)}>
                      {r.orderNo}
                    </button>
                    <span className="text-sm text-content">{r.customer}</span>
                    <span className="text-2xs text-content-subtle">{r.pieces} piece(s) · {r.items}</span>
                    {r.deliveryDate && (
                      <span className={`text-xs font-semibold ${r.overdue ? 'text-danger-600' : 'text-content-muted'}`}>
                        due {r.deliveryDate}
                      </span>
                    )}
                    <button className="btn-primary btn-sm ml-auto" onClick={() => setSignOff(r)}>
                      <ClipboardCheck size={13} /> Inspect &amp; finish
                    </button>
                  </div>
                  {r.photos.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {r.photos.map((src) => (
                        <button key={src} type="button" onClick={() => setPhoto({ src, job: { itemName: r.items, orderNo: r.orderNo } })}>
                          <img src={assetUrl(src)} alt="" className="h-12 w-12 rounded border border-line object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {!r.trialDoneAt && (
                    <p className="mt-1 text-2xs text-warning-700">The trial has not been marked done on this order.</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {photo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPhoto(null)}>
          <div className="max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2 text-sm text-white">
              <Shirt size={15} /> {photo.job.itemName} · {photo.job.orderNo}
              <button className="ml-auto rounded-full bg-white/15 p-1" onClick={() => setPhoto(null)}><X size={16} /></button>
            </div>
            <img src={assetUrl(photo.src)} alt="" className="max-h-[80vh] rounded-lg" />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!signOff}
        onClose={() => setSignOff(null)}
        title="Finish this order?"
        message={signOff
          ? `You have checked ${signOff.items} for ${signOff.customer}. Order ${signOff.orderNo} will be marked finished in your name.`
          : ''}
        confirmLabel="Yes, it is finished"
        tone="primary"
        busy={completeMut.isPending}
        onConfirm={() => completeMut.mutate(signOff.invoiceId)}
      />
    </div>
  );
}

/**
 * Shown on every screen while a supervisor is working as someone else, so the
 * step-in is never mistaken for the worker's own session.
 */
export function ActingAsBanner() {
  const { logout } = useAuth();
  const name = localStorage.getItem('erp_acting_as');
  const back = localStorage.getItem('erp_supervisor_token');
  if (!name || !back) return null;
  return (
    <div className="no-print flex items-center gap-2 bg-warning-500 px-4 py-1.5 text-xs font-semibold text-white">
      You are on {name}'s screen.
      <button
        className="ml-auto rounded bg-white/20 px-2 py-0.5"
        onClick={() => {
          localStorage.setItem('erp_token', back);
          localStorage.removeItem('erp_supervisor_token');
          localStorage.removeItem('erp_acting_as');
          window.location.assign(localStorage.getItem('erp_acting_from') || '/supervisor');
        }}
      >
        Back to my screen
      </button>
      <button className="rounded bg-white/20 px-2 py-0.5" onClick={logout}>Sign out</button>
    </div>
  );
}
